import { Document, Page, pdf } from "@react-pdf/renderer";
import { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@material-ui/core";
import PdfFooter from "../containers/User/PortInformation/pdfFooter";
import PdfHeader from "../containers/User/PortInformation/pdfHeader";
import { toastError, toastInfo } from "../utils";

export const makePdfBlob = (content, orientation = "portrait") =>
  pdf(
    <Document>
      <Page size="A4" orientation={orientation} style={{ paddingBottom: 35 }}>
        <PdfHeader />
        {content}
        <PdfFooter />
      </Page>
    </Document>
  ).toBlob();

export const downloadPdf = (blob, filename) => {
  const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
  const element = document.createElement("a");
  element.href = url;
  element.setAttribute("download", filename);
  element.style.display = "none";
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

export const canSharePdf = (blob, filename) => {
  try {
    return Boolean(navigator.share && navigator.canShare?.({
      files: [new File([blob], filename, { type: "application/pdf" })],
    }));
  } catch {
    return false;
  }
};

export const usePdfShare = () => {
  const [pending, setPending] = useState(null);
  const close = () => setPending(null);
  const prepareShare = async (blob, filename) => {
    if (!canSharePdf(blob, filename)) return sharePdf(blob, filename);
    if (navigator.userActivation?.isActive) return sharePdf(blob, filename);
    setPending({ blob, filename });
  };
  const confirmShare = async () => {
    const file = pending;
    if (!file) return;
    setPending(null);
    try {
      await sharePdf(file.blob, file.filename);
    } catch {
      toastError("Could not share the PDF. Please try Download PDF.");
    }
  };
  const shareDialog = (
    <Dialog open={Boolean(pending)} onClose={close} aria-labelledby="pdf-share-title" maxWidth="xs" fullWidth>
      <DialogTitle id="pdf-share-title">PDF ready to share</DialogTitle>
      <DialogContent>
        <DialogContentText>Tap Share PDF to choose an app on your device.</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button onClick={confirmShare} color="primary" variant="contained">
          Share PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
  return { prepareShare, shareDialog };
};

// Opens the device's share sheet (WhatsApp, Mail, ...) where the browser can share files;
// otherwise the PDF is downloaded instead.
export const sharePdf = async (blob, filename) => {
  if (canSharePdf(blob, filename)) {
    try {
      const file = new File([blob], filename, { type: "application/pdf" });
      await navigator.share({ files: [file], title: filename });
      return;
    } catch (err) {
      if (err?.name === "AbortError") return;
    }
  }
  downloadPdf(blob, filename);
  toastInfo("Sharing isn't available here, so the PDF was downloaded.");
};
